import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { DatePicker, Button, Select } from "antd";
import { toast } from "react-toastify";
import { getUser, getFileFromS3WithUrl, pause } from "../../../utils/helpers";
import { generateInvoiceData, getInvoiceHistory } from "../../../actions/invoice";
import { getUserList } from "../../../actions/sendReports";
import { getSearchOptions } from "../../../actions/batchReports";
import InvoiceFilterDropdown from "./components/InvoiceFilterDropdown";
import InvoiceDataModal from "./InvoiceDataModal";
import InvoiceHistory from "./InvoiceHistory";
import useAbortController from "../../Common/hooks/useAbortController";
// import FileSaver from "file-saver";
// import * as XLSX from "xlsx";

import "./Invoice.css";

const { RangePicker } = DatePicker;

export default function Invoice() {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().subtract(1, "month"), dayjs()]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceModalData, setInvoiceModalData] = useState([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState("");
  const [createByUserOptions, setCreateByUserOptions] = useState([{ value: "", label: "All" }]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerListWithID, setCustomerListWithID] = useState([]);

  // Invoice history state
  const [invoiceHistoryLoading, setInvoiceHistoryLoading] = useState(false);
  const [invoiceHistoryData, setInvoiceHistoryData] = useState(null);

  const { setNewAbortController } = useAbortController();
  const user = getUser();

  /**
   * Fetch user list for Created By filter
   */
  useEffect(() => {
    const fetchUserList = async () => {
      const response = await getUserList();
      if (response.success) {
        const userList = response.results;
        const createByOptions = userList?.sort((a, b) => {
          const nameA = `${a.first_name} ${a.last_name}`.trim().toLowerCase();
          const nameB = `${b.first_name} ${b.last_name}`.trim().toLowerCase();
          return nameA.localeCompare(nameB);
        }).reduce((acc, u) => {
          const label = `${u.first_name} ${u.last_name}`.trim();
          acc.push({ value: u.id, label });
          return acc;
        }, [{ value: "", label: "All" }]);
        setCreateByUserOptions(createByOptions);
      } else {
        toast.error(response.message || "Failed to fetch user list");
      }
    };
    fetchUserList();
  }, []);

  /**
   * Fetch customer list for Customer filter (same as Batch Reports)
   */
  useEffect(() => {
    const fetchCustomerList = async () => {
      try {
      const response = await getSearchOptions();
      if (!response.success) {
        toast.error(response.message);
        return;
      }
      if (response && response.success) {
        const list = response.list || [];
        const customerList = list.map((item) => ({
          value: `${item.company} (${item.customer_id})`,
          option: item,
        }));
        setCustomerListWithID(customerList);
      }
      } catch (error) {
        toast.error("Failed to fetch customer list");
      }
    };
    fetchCustomerList();
  }, []);

  /**
   * Handle Customer filter change
   * @param {string} customerId - Selected customer_id or "" on clear
   */
  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId || "");
  };

  /**
   * Handle Created By filter change
   * @param {string} value - Selected user ID
   */
  const handleCreatedByChange = (value) => {
    setSelectedCreatedBy(value);
  };

  /**
   * Convert JSON to Excel
   * Note: Requires imports: import FileSaver from "file-saver"; import * as XLSX from "xlsx";
   * @param {Array} jsonData - The JSON data to convert
   * @param {string} fileName - The file name to save the Excel file as
   */
  // const convertJsonToExcel = (jsonData, fileName) => {
  //   const ws = XLSX.utils.json_to_sheet(jsonData);
  //   const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  //   const excelBuffer = XLSX.write(wb, {
  //     bookType: "xlsx",
  //     type: "array",
  //   });
  //   const file = new Blob([excelBuffer], {
  //     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //   });
  //
  //   FileSaver.saveAs(file, `${fileName}`);
  // };

  /**
   * Fetch invoice history from API - just fetch and pass raw response
   */
  const fetchInvoiceHistory = useCallback(async () => {
    setInvoiceHistoryLoading(true);
    const newAbortController = setNewAbortController();
    const params = {};

    if (dateRange && dateRange[0] && dateRange[1]) {
      params.date_range = `${dateRange[0].format("MM/DD/YYYY")}-${dateRange[1].format("MM/DD/YYYY")}`;
    }
    if (selectedCreatedBy) {
      params.created_by = selectedCreatedBy;
    }
    if (selectedCustomerId) {
      params.customer_id = selectedCustomerId;
    }

    const response = await getInvoiceHistory(params, newAbortController.signal);
    if (response.canceled) {
      return;
    }
    setInvoiceHistoryData(response);
    setInvoiceHistoryLoading(false);
  }, [dateRange, selectedCreatedBy, selectedCustomerId]); // eslint-disable-line
  // Note: setNewAbortController intentionally excluded we only call it, don't read from it because creates loop.

  /**
   * Fetch invoice history when filters change
   */
  useEffect(() => {
    fetchInvoiceHistory();
  }, [fetchInvoiceHistory]);

  /**
   * Download files from invoice_list using presigned URLs
   * @param {Array} invoiceList - Array of invoice objects with excel_file_url and excel_file
   */
  const handleInvoiceDataDownload = async (invoiceList) => {
    if (!invoiceList || !Array.isArray(invoiceList) || invoiceList.length === 0) {
      toast.info("No data");
      return;
    }

    let downloadedCount = 0;
    let successCount = 0;
    let failedCount = 0;

    // eslint-disable-next-line no-await-in-loop
    for (let i = 0; i < invoiceList.length; i++) {
      const item = invoiceList[i];
      const { excel_file_url: presignedUrl, excel_file: fileName } = item;

      if (presignedUrl) {
        try {
          const { success, blob } = await getFileFromS3WithUrl("", presignedUrl); // eslint-disable-line no-await-in-loop
          if (success && blob) {
            // Create download link and trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || `invoice_${i + 1}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
            successCount++;
          } else {
            failedCount++;
          }
        } catch (error) {
          failedCount++;
        }
      } else {
        failedCount++;
      }

      // Rate limiting: pause after every 10 downloads
      downloadedCount++;
      if (downloadedCount >= 10) {
        // eslint-disable-next-line no-await-in-loop
        await pause(1500);
        downloadedCount = 0;
      }
    }

    // Show summary toast
    if (successCount > 0 && failedCount === 0) {
      toast.success(`Successfully downloaded ${successCount} file(s)`);
    } else if (successCount > 0 && failedCount > 0) {
      toast.warning(`Downloaded ${successCount} file(s), ${failedCount} failed`);
    } else if (failedCount > 0) {
      toast.error(`Failed to download ${failedCount} file(s)`);
    }
  };

  /**
   * Generate invoice data
   */
  const apiGenerateInvoiceData = async () => {
    setLoading(true);
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      toast.error("Select a Valid Date Range");
      setLoading(false);
      return;
    }
    const payload = {
      date_range: `${dateRange[0].format("MM/DD/YYYY")}-${dateRange[1].format("MM/DD/YYYY")}`,
      user_id: user.id,
    };
    if (selectedCustomerId) {
      payload.customer_id = selectedCustomerId;
    }
    const response = await generateInvoiceData(payload);
    if (response.success) {
      // Download Excel files from presigned URLs
      await handleInvoiceDataDownload(response.invoice_list);
      if (response?.unknowns && response.unknowns.length > 0) {
        // Show modal with pending data
        setInvoiceModalData(response.unknowns);
        setShowInvoiceModal(true);
      }
      // Refresh invoice history after generating data
      fetchInvoiceHistory();
    } else {
      toast.error(response.message);
    }
    setLoading(false);
  };

  /**
   * Handles the date range selection
   * @param {Array} dates - The selected date range
   */
  const handleDateRange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange(dates);
    } else {
      setDateRange(null);
    }
  };

  const handleCloseInvoiceModal = (generateDataAagain) => {
    setShowInvoiceModal(false);
    setInvoiceModalData([]);
    if (generateDataAagain) {
      apiGenerateInvoiceData();
    }
  };


  return (
    <div className="InvoiceContainer">
      <div className="InvoiceHeader">
        <div className="InvoiceDateRange__Container">
          <label>Date Range</label>
          <RangePicker
            showNow
            allowClear={false}
            format="MM/DD/YYYY"
            value={dateRange}
            className="InvoiceDatePicker"
            onChange={(dates) => handleDateRange(dates)}
            placeholder={["Start date", "End date"]}
          />
        </div>
        <div className="InvoiceFilterDropdown__Container">
          <label>Customer</label>
          <div className="InvoiceCustomerFilter__Main">
            <Select
              allowClear
              showSearch
              className="InvoiceCustomerFilter__Select"
              popupClassName="InvoiceCustomerFilter__Select__Menu"
              placeholder="Search Company"
              options={customerListWithID}
              value={customerListWithID.find((opt) => String(opt.option.customer_id) === String(selectedCustomerId))?.value}
              onSelect={(_, data) => handleCustomerChange(data.option.customer_id)}
              onClear={() => handleCustomerChange("")}
              listHeight={320}
            />
          </div>
        </div>
        <InvoiceFilterDropdown
          label="Created By"
          withSearch
          allowClear
          options={createByUserOptions}
          value={selectedCreatedBy}
          onSelect={handleCreatedByChange}
          onClear={() => handleCreatedByChange("")}
        />
        <Button loading={loading} type="button" onClick={apiGenerateInvoiceData} className="InvoiceGenerateDataButton">
          Generate Data
        </Button>
      </div>
      <InvoiceHistory 
        historyData={invoiceHistoryData}
        loading={invoiceHistoryLoading}
      />
      <InvoiceDataModal
        open={showInvoiceModal}
        onCancel={handleCloseInvoiceModal}
        data={invoiceModalData}
      />
    </div>
  );
}
